import EventBus from '@/misc/event-bus'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import type {CustomWindow} from '@/misc/custom-window'
import {HttpPost} from '@/services/Service'
import {showToast} from "@/general";

declare let window: CustomWindow

let eventSent10: boolean = false
let eventSent30: boolean = false
let eventSent60: boolean = false
let eventSent300: boolean = false
let lostWebsocket: boolean = false
let lostWebsocketAt: Date | null = null
let reconnecting: number | undefined = undefined

window.Pusher = Pusher
window.Echo = new Echo({
  broadcaster: 'reverb',
  key: typeof REVERB_APP_KEY === 'undefined' ? import.meta.env.VITE_REVERB_APP_KEY : REVERB_APP_KEY,
  wsHost: typeof REVERB_HOST === 'undefined' ? import.meta.env.VITE_REVERB_HOST : REVERB_HOST,
  wsPort: typeof REVERB_PORT === 'undefined' ? import.meta.env.VITE_REVERB_PORT : REVERB_PORT,
  wssPort: typeof REVERB_PORT === 'undefined' ? import.meta.env.VITE_REVERB_PORT : REVERB_PORT,
  forceTLS: typeof REVERB_USE_TLS === 'undefined' ? import.meta.env.VITE_REVERB_USE_TLS : REVERB_USE_TLS,
  enabledTransports: ['ws', 'wss'],
})

/**
 * This fixes an issue with back/forward cache (bfcache).
 * See: https://web.dev/bfcache/
 */
window.addEventListener('pagehide', () => {
  window.Echo?.disconnect()
})

window.Echo.connector.pusher.connection.bind('state_change', function (states) {
  // Lost connection after being connected
  if (states.previous === 'connected' && states.current !== 'connected') {
    lostWebsocket = true
    lostWebsocketAt = new Date()
    // noinspection JSIgnoredPromiseFromCall
    triggerReconnection()
  }

  // Failed to establish initial connection
  if (states.previous === 'connecting' && states.current === 'unavailable') {
    // We temporarily show the message at Russcher Kraanverhuur for analysis
    if (['develop', 'staging', 'local'].includes(window.APP_ENV) || window.TENANT_UUID === 'bcba3653-9ef9-4132-9dfc-3942d08e1d3c') {
      showToast(
        `Mertis kon geen verbinding maken. Klik hier om te vernieuwen.`,
        'info',
        null,
        0,
        {
          onclick: () => {window.location.reload()}
        }
      )
    }
  }

  // Connection restored
  if (states.previous !== 'connected' && states.current === 'connected') {
    if (lostWebsocket) {
      resetInterval()
      lostWebsocketAt = new Date()
      lostWebsocket = false
      eventSent10 = false
      eventSent30 = false
      eventSent60 = false
      eventSent300 = false

      EventBus.$emit('websocket-reconnected')
    }
  }
})

export function resetInterval () {
  clearInterval(reconnecting)

  reconnecting = undefined
}

export async function triggerReconnection () {
  if (reconnecting !== undefined) return

  reconnecting = window.setInterval(() => {
    window.Echo?.connector.pusher.connect()

    const lostAtTime = lostWebsocketAt?.getTime()
    const nowTime = new Date().getTime()

    if (lostAtTime === undefined) return

    if ((Math.abs(lostAtTime - nowTime) / 1000 > 10) && !eventSent10) {
      // noinspection JSIgnoredPromiseFromCall
      reportException(`Connection lost for more than 10 seconds`)
      eventSent10 = true

      // We temporarily show the message at Russcher Kraanverhuur for analysis
      // We show the notification as early as 10 seconds, as it is very important that the events are real-time
      if (['develop', 'staging', 'local'].includes(window.APP_ENV) || window.TENANT_UUID === 'bcba3653-9ef9-4132-9dfc-3942d08e1d3c') {
        showToast(
          `Mertis heeft de verbinding verloren. Klik hier om te vernieuwen.`,
          'info',
          null,
          0,
          {
            onclick: () => {window.location.reload()}
          }
        )
      }
    }

    if ((Math.abs(lostAtTime - nowTime) / 1000 > 30) && !eventSent30) {
      // noinspection JSIgnoredPromiseFromCall
      reportException('Connection lost for more than 30 seconds')
      eventSent30 = true
    }

    if ((Math.abs(lostAtTime - nowTime) / 1000 > 60) && !eventSent60) {
      // noinspection JSIgnoredPromiseFromCall
      reportException('Connection lost for more than 60 seconds')
      eventSent60 = true
    }

    if ((Math.abs(lostAtTime - nowTime) / 1000 > 300) && !eventSent300) {
      // noinspection JSIgnoredPromiseFromCall
      reportException('Connection lost for more than 300 seconds')
      eventSent300 = true
    }
    
  }, 1000)
}

export const reportException = async (message) => {
  return await HttpPost('/helper/exceptions/report', {message})
}
